import { createContext, useContext } from 'react';

import type { ConfirmOptions } from '@/components/ConfirmDialog';
import type { ShareScope } from '@escala/domain/share';
import type { EventScope } from '@escala/domain/transitions';
import type { ScheduleEvent } from '@escala/domain/types';

export type PickerRequest = { eventId: string; roleId: string; replaceId: string | null };

/** As abas do painel do evento (a equipe vê as três; o Membro, só a escala). */
export type EventSheetTab = 'escala' | 'publico' | 'resumo';

/**
 * Camadas que qualquer tela abre: painel do evento, seletor de membro, compartilhar,
 * formulário de evento e confirmação. Ficam acima das rotas para que o dashboard, a
 * busca e as notificações abram o mesmo painel que a tela de escalas.
 */
export interface OverlayApi {
  /** Painel do evento, já numa aba (a equipe; o Membro fica na escala). */
  openEvent: (eventId: string, tab?: EventSheetTab) => void;
  /**
   * "Registrar público do culto" (Administrador): as contagens do culto, sem nomes. Com
   * `pick`, dá para trocar de culto no próprio formulário.
   */
  openAudience: (eventId: string, options?: { pick?: boolean }) => void;
  openPicker: (request: PickerRequest) => void;
  /** Compartilhar a escala destes eventos, com os filtros da tela que abriu. */
  openShare: (eventIds: string[], scope?: ShareScope) => void;
  /** "Sincronizar agenda" (as escalas do usuário no Google Agenda, celular ou Outlook). */
  openCalendar: () => void;
  /** "Instalar aplicativo" (o convite do Dashboard e de Minha agenda e o menu do perfil). */
  openInstall: () => void;
  /** "Privacidade e dados" (menu do usuário): consentimentos, comunicações e direitos (LGPD). */
  openPrivacy: () => void;
  newEvent: (date?: string) => void;
  editEvent: (event: ScheduleEvent, scope?: 'one' | 'series') => void;
  /** "Novo evento" já em "Nova data de um evento", com este evento único escolhido. */
  newOccurrence: (seriesId: string) => void;
  /** "Novo evento" já com a configuração deste (o "Duplicar" de Eventos): outro card. */
  duplicateEvent: (event: ScheduleEvent) => void;
  /**
   * Excluir esta data: a confirmação simples no evento de uma data só; com mais datas, só
   * esta, esta e as próximas ou todas (`DeleteEventDialog`, aberto em `initial`).
   */
  deleteEvent: (event: ScheduleEvent, initial?: EventScope) => void;
  confirm: (options: ConfirmOptions) => void;
}

export const OverlayContext = createContext<OverlayApi | null>(null);

export function useOverlays(): OverlayApi {
  const api = useContext(OverlayContext);

  if (!api) {
    throw new Error('useOverlays precisa estar dentro de <OverlayProvider>.');
  }

  return api;
}
